import { FC, useEffect, useMemo, useRef, useState } from "react";
import classes from './calendarLessonCreate.module.scss'
import { calendarService, ICalendarFilter, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { workTgService } from "../../../entities/workTg";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { IItem } from "../../../shared/model/types";
import { MyButton } from "../../../shared/ui/button";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { formatTutorLoad } from "../../../shared/lib/helpers/formatTutorLoad";

interface IProps {
    startsAt: Date;
    durationSlots: number;
    filter: ICalendarFilter;
    filterOptions: IGetCalendarFilterOptionsResponse;
    formatTime: (date: Date) => string;
    onCreated: () => void;
    onCancel: () => void;
    onMount?: () => void;
}

const getSelectedOptions = <TOption extends {id: string}>(options: TOption[], selectedIds: string[]): TOption[] => {
    if(!selectedIds.length) return options

    return options.filter(option => selectedIds.includes(option.id))
}

const getSelectedItems = (selectedId: string): string[] => {
    return selectedId ? [selectedId] : []
}

const getSingleSelectedId = (items: IItem<string>[], selectedIds: string[]): string => {
    if(selectedIds.length !== 1) return ''

    return items.some(item => item.id === selectedIds[0])
        ? selectedIds[0]
        : ''
}

const getInitialSelectedId = (items: IItem<string>[], selectedIds: string[]): string => {
    const selectedId = getSingleSelectedId(items, selectedIds)
    if(selectedId) return selectedId

    return items.length === 1 ? items[0].id : ''
}

const getTgAccountName = (tgAccount: IGetCalendarFilterOptionsResponse['tgAccounts'][number]): string => {
    const title = tgAccount.title?.trim()
    const username = tgAccount.username?.trim().replace(/^@/, '')

    if(title && title !== '@') return title
    if(username) return `@${username}`

    return `TG #${tgAccount.id}`
}

const hasReadableTgAccountName = (tgAccount: IGetCalendarFilterOptionsResponse['tgAccounts'][number]): boolean => {
    return Boolean(
        (tgAccount.title && tgAccount.title.trim() && tgAccount.title.trim() !== '@') ||
        (tgAccount.username && tgAccount.username.trim())
    )
}

const getTutorsForTgAccount = (
    tutors: IGetCalendarFilterOptionsResponse['tutors'],
    tgAccountId: string,
) => tutors.filter(tutor => !tgAccountId || tutor.tgAccountId === tgAccountId)

type TCreateStage = 'userId' | 'studentId' | 'tagId' | null

export const CalendarLessonCreate: FC<IProps> = ({
    startsAt, durationSlots, filter, filterOptions,
    formatTime, onCreated, onCancel, onMount,
}) => {

    const [tgAccountId, setTgAccountId] = useState<string>('')
    const [userId, setUserId] = useState<string>('')
    const [studentId, setStudentId] = useState<string>('')
    const [tagId, setTagId] = useState<string>('')
    const [workTgNamesById, setWorkTgNamesById] = useState<Record<string, string>>({})
    const [tutorOptions, setTutorOptions] = useState<IGetCalendarFilterOptionsResponse['tutors']>([])
    const [studentOptions, setStudentOptions] = useState<IGetCalendarFilterOptionsResponse['students']>([])
    const [tagOptions, setTagOptions] = useState<IGetCalendarFilterOptionsResponse['tags']>([])
    const [stage, setStage] = useState<TCreateStage>(null)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [error, setError] = useState<string>('')
    const optionsControllerRef = useRef<AbortController | null>(null)

    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const endsAt = new Date(startsAt.getTime() + durationSlots * 30 * 60 * 1000)
    const isLoading = isSaving || stage !== null

    const tgAccountItems = useMemo<IItem<string>[]>(() => (
        getSelectedOptions(filterOptions.tgAccounts, filter.tgAccountIds)
            .map(tgAccount => ({
                id: tgAccount.id,
                name: workTgNamesById[tgAccount.id] || getTgAccountName(tgAccount),
            }))
    ), [filter.tgAccountIds, filterOptions.tgAccounts, workTgNamesById])

    const tutorItems = useMemo<IItem<string>[]>(() => (
        tutorOptions
            .map(tutor => ({id: tutor.userId, name: tutor.fullName, secondaryText: formatTutorLoad(tutor)}))
    ), [tutorOptions])

    const studentItems = useMemo<IItem<string>[]>(() => (
        getSelectedOptions(studentOptions, filter.studentIds)
            .map(student => ({id: student.id, name: student.fullName}))
    ), [filter.studentIds, studentOptions])

    const tagItems = useMemo<IItem<string>[]>(() => (
        getSelectedOptions(tagOptions, filter.tagIds)
            .map(tag => ({id: tag.id, name: tag.name}))
    ), [filter.tagIds, tagOptions])

    useEffect(() => {
        onMount?.()
    }, [onMount])

    useEffect(() => {
        const shouldLoadWorkTgNames = getSelectedOptions(filterOptions.tgAccounts, filter.tgAccountIds)
            .some(tgAccount => !hasReadableTgAccountName(tgAccount))

        if(!shouldLoadWorkTgNames) return

        let isActual = true

        workTgService.getAll()
            .then(workTgs => {
                if(!isActual) return

                setWorkTgNamesById(workTgs.reduce<Record<string, string>>((acc, workTg) => {
                    acc[String(workTg.id)] = workTg.name
                    return acc
                }, {}))
            })
            .catch(error => {
                console.log(error)
            })

        return () => {
            isActual = false
        }
    }, [filter.tgAccountIds, filterOptions.tgAccounts])

    const getSelectedFilter = (
        nextTgAccountId: string,
        nextUserId = '',
        nextStudentId = '',
    ): ICalendarFilter => ({
        tgAccountIds: getSelectedItems(nextTgAccountId),
        userIds: getSelectedItems(nextUserId),
        studentIds: getSelectedItems(nextStudentId),
        tagIds: [],
        includeCancelled: filter.includeCancelled,
        includeRescheduled: filter.includeRescheduled,
    })

    const requestOptions = async (
        selected: ICalendarFilter,
        nextStage: Exclude<TCreateStage, null>,
        controller: AbortController,
    ): Promise<IGetCalendarFilterOptionsResponse> => {
        setStage(nextStage)

        return await calendarService.getCalendarFilterOptions({selected}, controller.signal)
    }

    const loadTagOptions = async (
        nextTgAccountId: string,
        nextUserId: string,
        nextStudentId: string,
        controller: AbortController,
    ) => {
        const response = await requestOptions(
            getSelectedFilter(nextTgAccountId, nextUserId, nextStudentId),
            'tagId',
            controller,
        )
        if(controller.signal.aborted) return

        const nextOptions = getSelectedOptions(response.tags, filter.tagIds)
        const nextItems = nextOptions.map(tag => ({id: tag.id, name: tag.name}))

        setTagOptions(nextOptions)
        setTagId(getSingleSelectedId(nextItems, filter.tagIds))
    }

    const loadStudentOptions = async (
        nextTgAccountId: string,
        nextUserId: string,
        controller: AbortController,
    ) => {
        const response = await requestOptions(
            getSelectedFilter(nextTgAccountId, nextUserId),
            'studentId',
            controller,
        )
        if(controller.signal.aborted) return

        const nextOptions = getSelectedOptions(response.students, filter.studentIds)
        const nextItems = nextOptions.map(student => ({id: student.id, name: student.fullName}))
        const nextStudentId = getSingleSelectedId(nextItems, filter.studentIds)

        setStudentOptions(nextOptions)
        setStudentId(nextStudentId)

        if(nextStudentId){
            await loadTagOptions(nextTgAccountId, nextUserId, nextStudentId, controller)
        }
    }

    const loadTutorOptions = async (
        nextTgAccountId: string,
        controller: AbortController,
    ) => {
        const response = await requestOptions(
            getSelectedFilter(nextTgAccountId),
            'userId',
            controller,
        )
        if(controller.signal.aborted) return

        const nextOptions = getTutorsForTgAccount(response.tutors, nextTgAccountId)
        const nextItems = nextOptions.map(tutor => ({id: tutor.userId, name: tutor.fullName, secondaryText: formatTutorLoad(tutor)}))
        const nextUserId = getSingleSelectedId(nextItems, filter.userIds)

        setTutorOptions(nextOptions)
        setUserId(nextUserId)

        if(nextUserId){
            await loadStudentOptions(nextTgAccountId, nextUserId, controller)
        }
    }

    const showOptionsError = (requestError: unknown) => {
        console.log(requestError)

        if(requestError instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: requestError.message, type: 'error'})
        }
        else if(requestError instanceof MyError){
            setGlobalMessage({message: requestError.message, type: 'error'})
        }
        else{
            setError('Ошибка при загрузке списков для выбора')
        }
    }

    const startOptionsRequest = (request: (controller: AbortController) => Promise<void>) => {
        optionsControllerRef.current?.abort()

        const controller = new AbortController()
        optionsControllerRef.current = controller

        request(controller)
            .catch(requestError => {
                if((requestError as Error).name !== 'AbortError') showOptionsError(requestError)
            })
            .finally(() => {
                if(optionsControllerRef.current === controller && !controller.signal.aborted){
                    setStage(null)
                }
            })
    }

    const cancelOptionsRequest = () => {
        optionsControllerRef.current?.abort()
        optionsControllerRef.current = null
        setStage(null)
    }

    useEffect(() => {
        setUserId('')
        setStudentId('')
        setTagId('')
        setTutorOptions([])
        setStudentOptions([])
       setTagOptions([])

        const initialTgAccountId = getInitialSelectedId(tgAccountItems, filter.tgAccountIds)
        setTgAccountId(initialTgAccountId)

        if(initialTgAccountId){
            const initialTutorOptions = getTutorsForTgAccount(filterOptions.tutors, initialTgAccountId)
            setTutorOptions(initialTutorOptions)
            const initialUserId = getInitialSelectedId(
                initialTutorOptions.map(tutor => ({id: tutor.userId, name: tutor.fullName, secondaryText: formatTutorLoad(tutor)})),
                filter.userIds,
            )

            if(initialUserId){
                setUserId(initialUserId)
                startOptionsRequest(controller => loadStudentOptions(initialTgAccountId, initialUserId, controller))
            }
            else if(initialTutorOptions.length === 0){
                startOptionsRequest(controller => loadTutorOptions(initialTgAccountId, controller))
            }
        }

        return () => optionsControllerRef.current?.abort()
        // Выбор и списки инициализируются один раз при открытии формы.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSelectTgAccount = (item: IItem<string>) => {
        return (selected: boolean) => {
            const nextTgAccountId = selected ? item.id : ''

            cancelOptionsRequest()
            setError('')
            setTgAccountId(nextTgAccountId)
            setUserId('')
            setStudentId('')
            setTagId('')
            const nextTutorOptions = nextTgAccountId
                ? getTutorsForTgAccount(filterOptions.tutors, nextTgAccountId)
                : []
            setTutorOptions(nextTutorOptions)
            setStudentOptions([])
            setTagOptions([])

            if(nextTgAccountId){
                startOptionsRequest(controller => loadTutorOptions(nextTgAccountId, controller))
            }
        }
    }

    const onSelectTutor = (item: IItem<string>) => {
        return (selected: boolean) => {
            const nextUserId = selected ? item.id : ''

            cancelOptionsRequest()
            setError('')
            setUserId(nextUserId)
            setStudentId('')
            setTagId('')
            setStudentOptions([])
            setTagOptions([])

            if(tgAccountId && nextUserId){
                startOptionsRequest(controller => loadStudentOptions(tgAccountId, nextUserId, controller))
            }
        }
    }

    const onSelectStudent = (item: IItem<string>) => {
        return (selected: boolean) => {
            const nextStudentId = selected ? item.id : ''

            cancelOptionsRequest()
            setError('')
            setStudentId(nextStudentId)
            setTagId('')
            setTagOptions([])

            if(tgAccountId && userId && nextStudentId){
                startOptionsRequest(controller => loadTagOptions(tgAccountId, userId, nextStudentId, controller))
            }
        }
    }

    const onSelectTag = (item: IItem<string>) => {
        return (selected: boolean) => {
            setError('')
            setTagId(selected ? item.id : '')
        }
    }

    const createLesson = async () => {
        if(!tgAccountId || !userId || !studentId || !tagId){
            setError('Заполните рабочий Telegram, исполнителя, ученика и тег')
            return
        }

        if(
            !tgAccountItems.some(item => item.id === tgAccountId) ||
            !tutorItems.some(item => item.id === userId) ||
            !studentItems.some(item => item.id === studentId) ||
            !tagItems.some(item => item.id === tagId)
        ){
            setError('Выбранные данные занятия больше не связаны между собой. Выберите их заново')
            return
        }

        try{
            setIsSaving(true)
            setError('')

            await calendarService.createExtraLesson({
                slot: {
                    startsAt: startsAt.toISOString(),
                    durationSlots,
                },
                tgAccountId,
                userId,
                studentId,
                tagId,
            })

            setGlobalMessage({message: 'Занятие создано', type: 'ok'})
            onCreated()
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setError('Ошибка при создании занятия')
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <button type="button" className={classes.backButton} onClick={onCancel} disabled={isLoading}>
                    <span>‹</span>
                    Назад
                </button>

                <section className={classes.headerContent}>
                    <h2 className={classes.title}>Создание занятия</h2>
                    <p className={classes.time}>{formatTime(startsAt)}–{formatTime(endsAt)}</p>
                </section>
            </section>

            <section className={classes.form}>
                <section className={classes.field}>
                    <span className={classes.label}>Рабочий Telegram</span>
                    <DropDownListSelected
                        isLoading={isSaving}
                        items={tgAccountItems}
                        selectedIdItems={getSelectedItems(tgAccountId)}
                        onSelected={onSelectTgAccount}
                        search
                        searchPlaceholder="Поиск рабочего Telegram"
                    />
                </section>

                {Boolean(tgAccountId) && (
                    <section className={classes.field}>
                        <span className={classes.label}>Исполнитель</span>
                        <DropDownListSelected
                            isLoading={stage === 'userId'}
                            items={tutorItems}
                            selectedIdItems={getSelectedItems(userId)}
                            onSelected={onSelectTutor}
                            search
                            searchPlaceholder="Поиск исполнителя"
                        />
                    </section>
                )}

                {Boolean(userId) && (
                    <section className={classes.field}>
                        <span className={classes.label}>Ученик</span>
                        <DropDownListSelected
                            isLoading={stage === 'studentId'}
                            items={studentItems}
                            selectedIdItems={getSelectedItems(studentId)}
                            onSelected={onSelectStudent}
                            search
                            searchPlaceholder="Поиск ученика"
                        />
                    </section>
                )}

                {Boolean(studentId) && (
                    <section className={classes.field}>
                        <span className={classes.label}>Тег</span>
                        <DropDownListSelected
                            isLoading={stage === 'tagId'}
                            items={tagItems}
                            selectedIdItems={getSelectedItems(tagId)}
                            onSelected={onSelectTag}
                            search
                            searchPlaceholder="Поиск тега"
                        />
                    </section>
                )}
            </section>

            <section className={classes.actions}>
                <MyButton isLoading={isLoading} error={error} onClick={createLesson}>Создать</MyButton>
            </section>
        </section>
    )
}
